import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useVisibleGoals, useVisiblePriorities } from '../hooks/useScopedData';
import { useAppContext } from '../components/Layout';
import Spinner from '../components/Spinner';
import { isAdmin } from '../lib/visibility';
import Modal from '../components/Modal';
import { createPriority, updateDocFields, removeDoc } from '../lib/db';
import { useAuth } from '../auth/AuthProvider';
import type { StrategicPriority } from '../types';

export default function StrategicPlan() {
  const { profile, settings } = useAppContext();
  const { firebaseUser } = useAuth();
  const { data, loading } = useVisiblePriorities();
  const goalsQ = useVisibleGoals(profile);
  const [editing, setEditing] = useState<StrategicPriority | 'new' | null>(null);
  const admin = isAdmin(profile);
  const location = useLocation();
  const navigate = useNavigate();
  // The Dashboard's Council Strategic Plan strip links a specific Strategic
  // Area here instead of making the person hunt for it — same one-shot
  // router-state pattern as Goals & Tasks -> Initiatives' focusInitiativeId.
  const focusPriorityId = (location.state as { focusPriorityId?: string } | null)?.focusPriorityId;
  const [highlighted, setHighlighted] = useState<string | null>(null);

  useEffect(() => {
    if (!focusPriorityId) return;
    const target = data.find((p) => p.id === focusPriorityId);
    if (!target) return; // still loading — retry once data updates
    // Clearing router state here changes `focusPriorityId` on the next
    // render, which re-runs THIS effect (it's in the dependency array) —
    // so any highlight-clearing timer started here would get cancelled by
    // React's cleanup-on-rerun before it ever fires. The decay timer lives
    // in its own effect below, keyed only on `highlighted`, so it's
    // unaffected by this navigate-triggered rerun.
    navigate(location.pathname, { replace: true });
    setHighlighted(focusPriorityId);
    const t = setTimeout(() => {
      document.getElementById(`strategic-area-${focusPriorityId}`)?.scrollIntoView({ block: 'center' });
    }, 0);
    return () => clearTimeout(t);
  }, [focusPriorityId, data, location.pathname, navigate]);

  useEffect(() => {
    if (!highlighted) return;
    const t = setTimeout(() => setHighlighted(null), 2200);
    return () => clearTimeout(t);
  }, [highlighted]);

  const goalCount = useMemo(() => {
    const map = new Map<string, number>();
    for (const g of goalsQ.data) {
      map.set(g.strategicPriorityId, (map.get(g.strategicPriorityId) ?? 0) + 1);
    }
    return map;
  }, [goalsQ.data]);

  const active = data.filter((p) => p.status === 'active');
  const archived = data.filter((p) => p.status !== 'active');

  return (
    <>
      <div className="card">
        <div className="row">
          <div>
            <h2>Council Strategic Plan</h2>
            <div className="subtitle">Council's multi-year strategy and measurable goals.</div>
          </div>
          {admin && (
            <button className="btn primary" onClick={() => setEditing('new')}>
              + New Strategic Area
            </button>
          )}
        </div>
      </div>

      {loading && <div className="empty"><Spinner label="Loading…" /></div>}
      {!loading && data.length === 0 && (
        <div className="card empty">No Strategic Areas yet.</div>
      )}

      {active.map((p) => (
        <div
          className={`card${highlighted === p.id ? ' is-focused' : ''}`}
          id={`strategic-area-${p.id}`}
          key={p.id}
        >
          <div className="row">
            <div>
              <b>{p.title}</b>{' '}
              <span className="badge neutral">{p.programYear}</span>{' '}
              <span className="subtitle">
                {goalCount.get(p.id) ?? 0} goal{(goalCount.get(p.id) ?? 0) === 1 ? '' : 's'}
              </span>
              <p style={{ margin: '6px 0 0', color: 'var(--muted)' }}>{p.description}</p>
            </div>
            {admin && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn" onClick={() => setEditing(p)}>
                  Edit
                </button>
                <button
                  className="btn"
                  onClick={() => updateDocFields('strategicPriorities', p.id, { status: 'archived' })}
                >
                  Archive
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {archived.length > 0 && (
        <div className="card">
          <b>Archived</b>
          {archived.map((p) => (
            <div key={p.id} style={{ padding: '6px 0', color: 'var(--muted)' }}>
              {p.title} · {p.programYear}
              {admin && (
                <button
                  className="btn"
                  style={{ marginLeft: 8 }}
                  onClick={() => updateDocFields('strategicPriorities', p.id, { status: 'active' })}
                >
                  Restore
                </button>
              )}
              {admin && (
                <button
                  className="btn danger"
                  style={{ marginLeft: 6 }}
                  onClick={() => {
                    if (confirm('Delete this Strategic Area permanently?')) {
                      void removeDoc('strategicPriorities', p.id);
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <StrategicAreaForm
          initial={editing === 'new' ? null : editing}
          programYear={settings.currentProgramYear}
          onClose={() => setEditing(null)}
          onSave={async (vals) => {
            if (editing === 'new') {
              await createPriority({
                ...vals,
                status: 'active',
                createdBy: firebaseUser?.email?.toLowerCase() ?? 'unknown',
              });
            } else {
              await updateDocFields('strategicPriorities', editing.id, vals);
            }
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function StrategicAreaForm({
  initial,
  programYear,
  onClose,
  onSave,
}: {
  initial: StrategicPriority | null;
  programYear: string;
  onClose: () => void;
  onSave: (v: { title: string; description: string; programYear: string }) => Promise<void>;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [year, setYear] = useState(initial?.programYear ?? programYear);
  const [busy, setBusy] = useState(false);

  return (
    <Modal title={initial ? 'Edit Strategic Area' : 'New Strategic Area'} onClose={onClose}>
      <label>Title</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} />
      <label>Description</label>
      <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      <label>Program year</label>
      <input value={year} onChange={(e) => setYear(e.target.value)} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={busy || !title.trim()}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave({ title: title.trim(), description: description.trim(), programYear: year.trim() });
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
      </div>
    </Modal>
  );
}
